import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { settings } from '../settings';
import { RootState } from '../redux/store';

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const providersApi = createApi({
    reducerPath: 'Proveedores',
    tagTypes: ['Providers'],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getProviders: builder.query({
            query: (params) => ({
                url: `proveedores`,
                params
            }),
            providesTags: ['Providers']
        }),
        getAllProviders: builder.query({
            query: () => ({
                url: `proveedores/all`,
                method: 'GET'
            }),
            providesTags: ['Providers']
        }),
        registerProvider: builder.mutation({
            query: ({ body }: any) => ({
                url: `proveedores`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Providers'])
        }),
        editProvider: builder.mutation({
            query: ({ body, providerID }: any) => ({
                url: `proveedores/${providerID}`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Providers'])
        }),
        deactivateProvider: builder.mutation({
            query: ({ providerID }: any) => ({
                url: `proveedores/${providerID}/activacion`,
                body: { activar: false },
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Providers'])
        }),
        activateProvider: builder.mutation({
            query: ({ providerID }: any) => ({
                url: `proveedores/${providerID}/activacion`,
                body: { activar: true },
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Providers'])
        })
    })
});

export const {
    useGetProvidersQuery,
    useGetAllProvidersQuery,
    useRegisterProviderMutation,
    useEditProviderMutation,
    useDeactivateProviderMutation,
    useActivateProviderMutation
} = providersApi;
